import {
  useCallback,
  useEffect,
  useState,
  type FormEvent,
} from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../auth/AuthProvider";
import {
  useOrganization,
  type ModuleKey,
} from "../organization/OrganizationProvider";

type MemberRole = "owner" | "admin" | "manager" | "staff" | "viewer";

type MemberRow = {
  membership_id: string;
  user_id: string;
  email: string;
  role: MemberRole;
  status: "active" | "inactive" | "archived";
  created_at: string;
  updated_at: string;
};

type InvitationRow = {
  invitation_id: string;
  email: string;
  role: Exclude<MemberRole, "owner">;
  status: string;
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
  invited_by: string | null;
};

type OrganizationProfile = {
  id: string;
  name: string;
  legal_name: string | null;
  tax_id: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  country_code: string;
  currency_code: string;
  timezone: string;
  status: string;
};

type AccessHistoryRow = {
  id: string;
  target_kind: "member" | "invitation" | "module";
  target_id: string;
  target_email: string | null;
  action: string;
  old_role: MemberRole | null;
  new_role: MemberRole | null;
  old_status: string | null;
  new_status: string | null;
  performed_by: string | null;
  occurred_at: string;
};

const MODULE_LABELS: Record<ModuleKey, { title: string; description: string }> = {
  crm: {
    title: "Clientes y activos",
    description: "Clientes, historial de interacciones y activos vinculados.",
  },
  catalog: {
    title: "Catálogo y maestros",
    description: "Productos, servicios, proveedores y almacenes.",
  },
  quotations: {
    title: "Cotizaciones",
    description: "Propuestas comerciales y aprobación previa.",
  },
  workshop: {
    title: "Taller",
    description: "Órdenes de servicio, técnicos, repuestos y evidencias.",
  },
  inventory: {
    title: "Inventario",
    description: "Stock, movimientos y consumos.",
  },
  sales: {
    title: "Ventas",
    description: "Ventas, cobros e integración operativa.",
  },
  purchases: {
    title: "Compras e importaciones",
    description: "Órdenes de compra, recepciones e importaciones.",
  },
  accounting: {
    title: "Finanzas y contabilidad",
    description: "Contabilidad, tesorería, conciliación, reportes y fiscal.",
  },
};

const MODULE_KEYS = Object.keys(MODULE_LABELS) as ModuleKey[];

const ROLE_LABELS: Record<MemberRole, string> = {
  owner: "Propietario",
  admin: "Administrador",
  manager: "Responsable",
  staff: "Equipo",
  viewer: "Solo lectura",
};

export function SettingsPage() {
  const { user } = useAuth();
  const {
    selectedOrganization,
    enabledModules,
    modulesLoading,
    refreshModules,
    refreshOrganizations,
  } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? null;

  const [members, setMembers] = useState<MemberRow[]>([]);
  const [invitations, setInvitations] = useState<InvitationRow[]>([]);
  const [accessHistory, setAccessHistory] = useState<AccessHistoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [email, setEmail] = useState("");
  const [role, setRole] =
    useState<Exclude<MemberRole, "owner">>("staff");
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteMessage, setInviteMessage] = useState<string | null>(null);
  const [memberBusyId, setMemberBusyId] = useState<string | null>(null);
  const [memberError, setMemberError] = useState<string | null>(null);
  const [memberMessage, setMemberMessage] = useState<string | null>(null);
  const [invitationBusyId, setInvitationBusyId] = useState<string | null>(null);
  const [moduleBusyKey, setModuleBusyKey] = useState<ModuleKey | null>(null);
  const [moduleError, setModuleError] = useState<string | null>(null);
  const [moduleMessage, setModuleMessage] = useState<string | null>(null);

  const [profile, setProfile] = useState<OrganizationProfile | null>(null);
  const [profileName, setProfileName] = useState("");
  const [profileLegalName, setProfileLegalName] = useState("");
  const [profileTaxId, setProfileTaxId] = useState("");
  const [profileEmail, setProfileEmail] = useState("");
  const [profilePhone, setProfilePhone] = useState("");
  const [profileAddress, setProfileAddress] = useState("");
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileMessage, setProfileMessage] = useState<string | null>(null);

  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    if (!organizationId) {
      setMembers([]);
      setInvitations([]);
      setAccessHistory([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError(null);

    const [membersResult, invitationsResult, accessHistoryResult] =
      await Promise.all([
        supabase.rpc("list_organization_users_ui", {
          p_organization_id: organizationId,
        }),
        supabase.rpc("list_organization_invitations_ui", {
          p_organization_id: organizationId,
        }),
        supabase.rpc("list_organization_access_history", {
          p_organization_id: organizationId,
          p_limit: 50,
        }),
      ]);

    const firstError =
      membersResult.error ??
      invitationsResult.error ??
      accessHistoryResult.error;

    if (firstError) {
      setLoadError(firstError.message);
    }

    setMembers(
      membersResult.error ? [] : ((membersResult.data ?? []) as MemberRow[]),
    );
    setInvitations(
      invitationsResult.error
        ? []
        : ((invitationsResult.data ?? []) as InvitationRow[]),
    );
    setAccessHistory(
      accessHistoryResult.error
        ? []
        : ((accessHistoryResult.data ?? []) as AccessHistoryRow[]),
    );
    setLoading(false);
  }, [organizationId]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const loadProfile = useCallback(async () => {
    if (!organizationId) {
      setProfile(null);
      return;
    }

    setProfileLoading(true);
    setProfileError(null);

    const { data, error } = await supabase.rpc("get_organization_profile", {
      p_organization_id: organizationId,
    });

    setProfileLoading(false);

    if (error) {
      setProfile(null);
      setProfileError(error.message);
      return;
    }

    const nextProfile = data as OrganizationProfile;
    setProfile(nextProfile);
    setProfileName(nextProfile.name ?? "");
    setProfileLegalName(nextProfile.legal_name ?? "");
    setProfileTaxId(nextProfile.tax_id ?? "");
    setProfileEmail(nextProfile.email ?? "");
    setProfilePhone(nextProfile.phone ?? "");
    setProfileAddress(nextProfile.address ?? "");
  }, [organizationId]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    setEmail("");
    setRole("staff");
    setInviteError(null);
    setInviteMessage(null);
  }, [organizationId]);

  async function changeOwnPassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPasswordError(null);
    setPasswordMessage(null);

    if (newPassword.length < 12) {
      setPasswordError("La nueva contraseña debe tener al menos 12 caracteres.");
      return;
    }

    if (newPassword !== newPasswordConfirm) {
      setPasswordError("Las contraseñas no coinciden.");
      return;
    }

    setPasswordSaving(true);

    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    setPasswordSaving(false);

    if (error) {
      setPasswordError(error.message);
      return;
    }

    setNewPassword("");
    setNewPasswordConfirm("");
    setPasswordMessage("Contraseña actualizada correctamente.");
  }

  async function saveProfile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!organizationId) return;

    setProfileSaving(true);
    setProfileError(null);
    setProfileMessage(null);

    const { error } = await supabase.rpc("update_organization_profile", {
      p_organization_id: organizationId,
      p_name: profileName,
      p_legal_name: profileLegalName.trim() || null,
      p_tax_id: profileTaxId.trim() || null,
      p_email: profileEmail.trim() || null,
      p_phone: profilePhone.trim() || null,
      p_address: profileAddress.trim() || null,
    });

    setProfileSaving(false);

    if (error) {
      setProfileError(error.message);
      return;
    }

    await Promise.all([loadProfile(), refreshOrganizations()]);
    setProfileMessage("Datos de empresa actualizados.");
  }

  async function handleInvite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!organizationId) return;

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setInviteError("Ingresa un correo.");
      return;
    }

    setInviting(true);
    setInviteError(null);
    setInviteMessage(null);

    const { data, error } = await supabase.functions.invoke(
      "invite-company-user",
      {
        body: {
          organization_id: organizationId,
          email: normalizedEmail,
          role,
          send_email: true,
        },
      },
    );

    setInviting(false);

    if (error) {
      setInviteError(error.message);
      return;
    }

    const result = data as {
      ok?: boolean;
      error?: string;
      email_sent?: boolean;
      email_state?: string;
      reused_pending_invitation?: boolean;
    } | null;

    if (!result?.ok) {
      setInviteError(result?.error ?? "No se pudo crear la invitación.");
      return;
    }

    setEmail("");

    if (result.email_sent) {
      setInviteMessage("Invitación creada y correo enviado.");
    } else if (result.reused_pending_invitation) {
      setInviteMessage("Ya existía una invitación pendiente para este correo.");
    } else {
      setInviteMessage(
        `Invitación creada. Estado de correo: ${result.email_state ?? "no enviado"}.`,
      );
    }

    await loadUsers();
  }

  async function changeMemberRole(
    member: MemberRow,
    nextRole: Exclude<MemberRole, "owner">,
  ) {
    setMemberBusyId(member.membership_id);
    setMemberError(null);
    setMemberMessage(null);

    const { error } = await supabase.rpc("update_organization_member_role", {
      p_membership_id: member.membership_id,
      p_role: nextRole,
    });

    setMemberBusyId(null);

    if (error) {
      setMemberError(error.message);
      return;
    }

    setMemberMessage(`Rol actualizado para ${member.email}.`);
    await loadUsers();
  }

  async function changeMemberStatus(member: MemberRow) {
    const nextStatus = member.status === "active" ? "inactive" : "active";

    setMemberBusyId(member.membership_id);
    setMemberError(null);
    setMemberMessage(null);

    const { error } = await supabase.rpc("set_organization_member_status", {
      p_membership_id: member.membership_id,
      p_status: nextStatus,
    });

    setMemberBusyId(null);

    if (error) {
      setMemberError(error.message);
      return;
    }

    setMemberMessage(
      nextStatus === "active"
        ? `Acceso reactivado para ${member.email}.`
        : `Acceso desactivado para ${member.email}.`,
    );
    await loadUsers();
  }

  async function toggleModule(moduleKey: ModuleKey) {
    if (!organizationId) return;

    const nextEnabled = !enabledModules.includes(moduleKey);

    setModuleBusyKey(moduleKey);
    setModuleError(null);
    setModuleMessage(null);

    const { error } = await supabase.rpc("set_organization_module_enabled", {
      p_organization_id: organizationId,
      p_module_key: moduleKey,
      p_enabled: nextEnabled,
    });

    setModuleBusyKey(null);

    if (error) {
      setModuleError(error.message);
      return;
    }

    await refreshModules();
    setModuleMessage(
      nextEnabled
        ? `${MODULE_LABELS[moduleKey].title} habilitado.`
        : `${MODULE_LABELS[moduleKey].title} deshabilitado.`,
    );
  }

  async function resendInvitation(invitation: InvitationRow) {
    if (!organizationId || invitation.status !== "pending") return;

    setInvitationBusyId(invitation.invitation_id);
    setInviteError(null);
    setInviteMessage(null);

    const { data, error } = await supabase.functions.invoke(
      "invite-company-user",
      {
        body: {
          organization_id: organizationId,
          email: invitation.email,
          role: invitation.role,
          send_email: true,
        },
      },
    );

    setInvitationBusyId(null);

    if (error) {
      setInviteError(error.message);
      return;
    }

    const result = data as {
      ok?: boolean;
      error?: string;
      email_sent?: boolean;
      email_state?: string;
      email_error?: string | null;
    } | null;

    if (!result?.ok) {
      setInviteError(result?.error ?? "No se pudo reenviar la invitación.");
      return;
    }

    if (result.email_sent) {
      const delivery =
        result.email_state === "MAGIC_LINK_SENT"
          ? "Enlace de acceso reenviado."
          : "Invitación reenviada por correo.";
      setInviteMessage(`${delivery} ${invitation.email}`);
    } else {
      setInviteError(
        result.email_error ??
          `No se pudo enviar el correo. Estado: ${result.email_state ?? "desconocido"}.`,
      );
    }

    await loadUsers();
  }

  async function cancelInvitation(invitation: InvitationRow) {
    setInvitationBusyId(invitation.invitation_id);
    setInviteError(null);
    setInviteMessage(null);

    const { error } = await supabase.rpc("cancel_organization_invitation", {
      p_invitation_id: invitation.invitation_id,
    });

    setInvitationBusyId(null);

    if (error) {
      setInviteError(error.message);
      return;
    }

    setInviteMessage(`Invitación cancelada para ${invitation.email}.`);
    await loadUsers();
  }

  if (!selectedOrganization) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Selecciona una empresa.</strong>
          <p>Necesitas una empresa activa para abrir Configuración.</p>
        </section>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Configuración</span>
          <h1>{selectedOrganization.name}</h1>
          <p>
            Usuarios y accesos.
            {selectedOrganization.taxId
              ? ` RUC ${selectedOrganization.taxId}.`
              : ""}
          </p>
        </div>
      </header>

      {loadError ? (
        <div className="inline-alert" role="alert">
          {loadError}
        </div>
      ) : null}

      <section className="settings-grid">
        <div className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Mi cuenta</span>
              <h2>Seguridad de acceso</h2>
              <p>{user?.email ?? "Usuario autenticado"}</p>
            </div>
          </div>

          <form className="account-password-form" onSubmit={changeOwnPassword}>
            <label>
              Nueva contraseña
              <input
                type="password"
                autoComplete="new-password"
                minLength={12}
                required
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
              />
            </label>

            <label>
              Confirmar contraseña
              <input
                type="password"
                autoComplete="new-password"
                minLength={12}
                required
                value={newPasswordConfirm}
                onChange={(event) => setNewPasswordConfirm(event.target.value)}
              />
            </label>

            {passwordError ? (
              <div className="form-error field-span-full">{passwordError}</div>
            ) : null}

            {passwordMessage ? (
              <div className="form-success field-span-full">{passwordMessage}</div>
            ) : null}

            <div className="form-actions field-span-full">
              <button
                className="primary-button"
                type="submit"
                disabled={passwordSaving}
              >
                {passwordSaving ? "Actualizando…" : "Cambiar contraseña"}
              </button>
            </div>
          </form>
        </div>

        <div className="panel-card settings-invite-panel">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Acceso</span>
              <h2>Invitar usuario</h2>
            </div>
          </div>

          <form className="settings-invite-form" onSubmit={handleInvite}>
            <label>
              Correo
              <input
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="persona@empresa.com"
              />
            </label>

            <label>
              Rol
              <select
                value={role}
                onChange={(event) =>
                  setRole(
                    event.target.value as Exclude<MemberRole, "owner">,
                  )
                }
              >
                <option value="admin">Administrador</option>
                <option value="manager">Responsable</option>
                <option value="staff">Equipo</option>
                <option value="viewer">Solo lectura</option>
              </select>
            </label>

            {inviteError ? <div className="form-error">{inviteError}</div> : null}
            {inviteMessage ? (
              <div className="form-success">{inviteMessage}</div>
            ) : null}

            <button
              className="primary-button"
              type="submit"
              disabled={inviting}
            >
              {inviting ? "Creando invitación…" : "Invitar"}
            </button>
          </form>
        </div>

        <div className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Empresa</span>
              <h2>Datos de la empresa</h2>
            </div>
          </div>

          {profileError ? <div className="form-error">{profileError}</div> : null}
          {profileMessage ? (
            <div className="form-success">{profileMessage}</div>
          ) : null}

          <form className="organization-profile-form" onSubmit={saveProfile}>
            <label>
              Nombre comercial
              <input
                required
                value={profileName}
                onChange={(event) => setProfileName(event.target.value)}
                disabled={profileLoading || profileSaving}
              />
            </label>
            <label>
              Razón social
              <input
                value={profileLegalName}
                onChange={(event) => setProfileLegalName(event.target.value)}
                disabled={profileLoading || profileSaving}
              />
            </label>
            <label>
              RUC / identificación fiscal
              <input
                value={profileTaxId}
                onChange={(event) => setProfileTaxId(event.target.value)}
                disabled={profileLoading || profileSaving}
              />
            </label>
            <label>
              Correo
              <input
                type="email"
                value={profileEmail}
                onChange={(event) => setProfileEmail(event.target.value)}
                disabled={profileLoading || profileSaving}
              />
            </label>
            <label>
              Teléfono
              <input
                value={profilePhone}
                onChange={(event) => setProfilePhone(event.target.value)}
                disabled={profileLoading || profileSaving}
              />
            </label>
            <label>
              Dirección
              <input
                value={profileAddress}
                onChange={(event) => setProfileAddress(event.target.value)}
                disabled={profileLoading || profileSaving}
              />
            </label>

            <div className="organization-profile-footer field-span-full">
              <span>
                {profile
                  ? `${profile.country_code} · ${profile.currency_code} · ${profile.timezone}`
                  : "Cargando datos…"}
              </span>
              <button
                className="primary-button"
                type="submit"
                disabled={
                  profileLoading ||
                  profileSaving ||
                  !(
                    selectedOrganization.isPlatformAdmin ||
                    selectedOrganization.role === "owner" ||
                    selectedOrganization.role === "admin"
                  )
                }
              >
                {profileSaving ? "Guardando…" : "Guardar empresa"}
              </button>
            </div>
          </form>
        </div>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Módulos</span>
            <h2>Disponibilidad por empresa</h2>
            <p>Activa únicamente las áreas que utilizará esta empresa.</p>
          </div>
        </div>

        {moduleError ? <div className="form-error">{moduleError}</div> : null}
        {moduleMessage ? (
          <div className="form-success">{moduleMessage}</div>
        ) : null}

        <div className="module-settings-grid">
          {MODULE_KEYS.map((moduleKey) => {
            const enabled = enabledModules.includes(moduleKey);
            const canManage =
              selectedOrganization.isPlatformAdmin ||
              selectedOrganization.role === "owner" ||
              selectedOrganization.role === "admin";

            return (
              <article className="module-setting-card" key={moduleKey}>
                <div>
                  <strong>{MODULE_LABELS[moduleKey].title}</strong>
                  <span>{MODULE_LABELS[moduleKey].description}</span>
                </div>
                <button
                  className={
                    enabled
                      ? "compact-button module-toggle-enabled"
                      : "compact-button"
                  }
                  type="button"
                  disabled={
                    modulesLoading ||
                    moduleBusyKey === moduleKey ||
                    !canManage
                  }
                  onClick={() => void toggleModule(moduleKey)}
                >
                  {moduleBusyKey === moduleKey
                    ? "Guardando…"
                    : enabled
                      ? "Activo"
                      : "Inactivo"}
                </button>
              </article>
            );
          })}
        </div>
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Miembros</span>
            <h2>{loading ? "Cargando…" : `${members.length} usuarios`}</h2>
          </div>
        </div>

        {memberError ? <div className="form-error">{memberError}</div> : null}
        {memberMessage ? (
          <div className="form-success">{memberMessage}</div>
        ) : null}

        <div className="member-grid">
          {members.map((member) => (
            <article className="member-card" key={member.membership_id}>
              <div>
                <strong>{member.email}</strong>
                <span>{ROLE_LABELS[member.role]}</span>
              </div>

              {member.role === "owner" ? (
                <span className="status-badge status-badge-approved">
                  Propietario protegido
                </span>
              ) : (
                <div className="member-access-controls">
                  <select
                    aria-label={`Rol de ${member.email}`}
                    value={member.role}
                    disabled={memberBusyId === member.membership_id}
                    onChange={(event) =>
                      void changeMemberRole(
                        member,
                        event.target.value as Exclude<MemberRole, "owner">,
                      )
                    }
                  >
                    <option value="admin">Administrador</option>
                    <option value="manager">Responsable</option>
                    <option value="staff">Equipo</option>
                    <option value="viewer">Solo lectura</option>
                  </select>

                  <button
                    className={
                      member.status === "active"
                        ? "compact-button compact-button-danger"
                        : "compact-button"
                    }
                    type="button"
                    disabled={memberBusyId === member.membership_id}
                    onClick={() => void changeMemberStatus(member)}
                  >
                    {memberBusyId === member.membership_id
                      ? "Guardando…"
                      : member.status === "active"
                        ? "Desactivar"
                        : "Reactivar"}
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
      </section>


      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Auditoría de acceso</span>
            <h2>Historial reciente</h2>
          </div>
        </div>

        {accessHistory.length === 0 && !loading ? (
          <div className="settings-empty">
            Todavía no hay cambios de acceso registrados.
          </div>
        ) : (
          <div className="access-history-list">
            {accessHistory.map((event) => {
              const roleChange =
                event.action === "role_changed" &&
                event.old_role &&
                event.new_role;
              const statusChange =
                event.action === "status_changed" &&
                event.old_status &&
                event.new_status;
              const invitationCreated =
                event.action === "invitation_created";
              const invitationStatus =
                event.action === "invitation_status_changed";

              return (
                <article className="access-history-item" key={event.id}>
                  <div>
                    <strong>
                      {event.target_kind === "module" &&
                      event.target_email &&
                      event.target_email in MODULE_LABELS
                        ? MODULE_LABELS[event.target_email as ModuleKey].title
                        : event.target_email ?? "Usuario"}
                    </strong>
                    <span>
                      {roleChange
                        ? `${ROLE_LABELS[event.old_role!]} → ${ROLE_LABELS[event.new_role!]}`
                        : statusChange
                          ? `${event.old_status} → ${event.new_status}`
                          : invitationCreated
                            ? "Invitación creada"
                            : invitationStatus
                              ? `Invitación: ${event.old_status} → ${event.new_status}`
                              : event.action === "module_status_changed"
                                ? `Módulo: ${event.old_status} → ${event.new_status}`
                                : event.action === "module_created"
                                  ? `Módulo creado: ${event.new_status}`
                                  : event.action}
                    </span>
                  </div>
                  <time>
                    {new Date(event.occurred_at).toLocaleString("es-PE")}
                  </time>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <section className="panel-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Invitaciones</span>
            <h2>Pendientes e historial</h2>
          </div>
        </div>

        {invitations.length === 0 && !loading ? (
          <div className="settings-empty">
            No hay invitaciones registradas para esta empresa.
          </div>
        ) : (
          <div className="invitation-grid">
            {invitations.map((invitation) => (
              <article
                className="invitation-card"
                key={invitation.invitation_id}
              >
                <div>
                  <strong>{invitation.email}</strong>
                  <span>{ROLE_LABELS[invitation.role]}</span>
                </div>
                <div className="invitation-state">
                  <span
                    className={
                      invitation.status === "pending"
                        ? "status-badge status-badge-expired"
                        : invitation.status === "accepted"
                          ? "status-badge status-badge-approved"
                          : "status-badge"
                    }
                  >
                    {invitation.status}
                  </span>
                  <small>
                    Vence {new Date(invitation.expires_at).toLocaleDateString("es-PE")}
                  </small>
                  {invitation.status === "pending" ? (
                    <div className="invitation-actions">
                      <button
                        className="compact-button"
                        type="button"
                        disabled={invitationBusyId === invitation.invitation_id}
                        onClick={() => void resendInvitation(invitation)}
                      >
                        {invitationBusyId === invitation.invitation_id
                          ? "Procesando…"
                          : "Reenviar invitación"}
                      </button>

                      <button
                        className="compact-button compact-button-danger"
                        type="button"
                        disabled={invitationBusyId === invitation.invitation_id}
                        onClick={() => void cancelInvitation(invitation)}
                      >
                        {invitationBusyId === invitation.invitation_id
                          ? "Procesando…"
                          : "Cancelar invitación"}
                      </button>
                    </div>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
